// EPOCH 29: CSR Lead Response Form Page
// Wrapper page for the unified CSR Lead Response Form that handles both new and follow-up modes
// Date: December 2024

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase/client';
import CSRLeadResponseForm from '../components/lead-responses/CSRLeadResponseForm';
import { LeadResponse } from '../types/lead-responses';

const CSRLeadResponseFormPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get mode and parent ID from URL params
  const mode = (searchParams.get('mode') as 'new' | 'followup') || 'new';
  const parentId = searchParams.get('parent');
  
  // Also check location state for parent response (from navigation)
  const stateParentResponse = location.state?.parentResponse as LeadResponse | undefined;
  
  const [parentResponse, setParentResponse] = useState<LeadResponse | undefined>(stateParentResponse);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load parent response if we have an ID but no response object
  useEffect(() => {
    const loadParentResponse = async () => {
      if (mode === 'followup' && parentId && !parentResponse) {
        setLoading(true);
        try {
          const { data, error } = await supabase.functions.invoke('lead-responses', {
            body: {
              action: 'get',
              id: parentId
            }
          });

          if (error) throw error;
          if (data?.error) throw new Error(data.error);

          setParentResponse(data.data);
        } catch (err) {
          console.error('Error loading parent response:', err);
          setError('Failed to load parent response. Please try again.');
        } finally {
          setLoading(false);
        }
      }
    };

    loadParentResponse();
  }, [mode, parentId, parentResponse]);

  const handleSuccess = (leadResponse: LeadResponse) => {
    // Navigate to the lead response detail page
    navigate(`/leads-inbox`, {
      state: {
        selectedLeadId: leadResponse.id,
        message: mode === 'followup' ? 'Follow-up added successfully!' : 'Lead response created successfully!'
      }
    });
  };

  const handleCancel = () => {
    // Navigate back to previous page or leads inbox
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/leads-inbox');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading parent response...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/leads-inbox')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Leads Inbox
          </button>
        </div>
      </div>
    );
  }

  if (mode === 'followup' && !parentResponse && !loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">Parent response is required for follow-up mode</p>
          <button
            onClick={() => navigate('/leads-inbox')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Leads Inbox
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Back button */}
      <div className="mb-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {mode === 'followup' ? 'Back to Lead Detail' : 'Back to Inbox'}
        </button>
      </div>

      {/* Form component with unified functionality */}
      <CSRLeadResponseForm
        mode={mode}
        parentResponse={parentResponse}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />
    </div>
  );
};

export default CSRLeadResponseFormPage;